import { memo } from 'react'

/**
 * GlassmorphFormField - Reusable glassmorphic form field component
 * 
 * Provides consistent glassmorphic styling for inputs, textareas, and selects
 * across the application. Eliminates duplicate className definitions.
 * 
 * Design Pattern:
 * - Semi-transparent backgrounds (bg-white/90)
 * - Backdrop blur effect (backdrop-blur-sm)
 * - Soft borders (border-gray-200/50)
 * - Rounded corners (rounded-lg)
 * - Subtle shadows with hover effects
 * - Smooth transitions
 * 
 * @param {string} label - Field label text
 * @param {string} name - Field name attribute
 * @param {string} type - Input type (text, email, textarea, select)
 * @param {string} value - Field value
 * @param {function} onChange - Change handler
 * @param {function} onFocus - Focus handler (optional)
 * @param {function} onBlur - Blur handler (optional)
 * @param {boolean} required - Whether field is required
 * @param {string} placeholder - Placeholder text
 * @param {string} error - Error message to display
 * @param {number} rows - Number of rows for textarea
 * @param {ReactNode} children - Options for select element
 * @param {string} className - Additional classes for the container
 * @param {string} helpText - Help text displayed below field
 * @param {boolean} disabled - Whether field is disabled
 */
const GlassmorphFormField = memo(function GlassmorphFormField({
  label,
  name,
  type = 'text',
  value,
  onChange,
  onFocus,
  onBlur,
  required = false,
  placeholder,
  error,
  rows = 4,
  children, // For select options
  className = '',
  helpText,
  disabled = false
}) {
  const baseInputClasses = "w-full px-3 py-2 bg-white/90 backdrop-blur-sm border border-gray-200/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm shadow-sm hover:bg-white transition-colors"
  const disabledClasses = disabled ? "disabled:bg-gray-100/80 disabled:cursor-not-allowed" : ""
  const inputClasses = `${baseInputClasses} ${disabledClasses}`

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      
      {type === 'textarea' ? (
        <textarea
          name={name}
          value={value}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          required={required}
          rows={rows}
          className={inputClasses}
          placeholder={placeholder}
          disabled={disabled}
        />
      ) : type === 'select' ? (
        <select
          name={name}
          value={value}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          required={required}
          className={inputClasses}
          disabled={disabled}
        >
          {children}
        </select>
      ) : (
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          required={required}
          className={inputClasses}
          placeholder={placeholder}
          disabled={disabled}
        />
      )}
      
      {error && (
        <p className="mt-1 text-xs text-red-600">{error}</p>
      )}
      
      {helpText && !error && (
        <p className="mt-1 text-xs text-gray-500">{helpText}</p>
      )}
    </div>
  )
})

export default GlassmorphFormField
